import type { PublicIntakeMetadata } from "@kollectivia/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { downloadPublicCensusTemplate } from "../../../lib/apiPublic";

import { IntakeForm } from "./IntakeForm";

export function CensusWorkbookIntake({
  token,
  metadata,
  onSuccess,
  onTokenInvalid,
}: {
  readonly token: string;
  readonly metadata: PublicIntakeMetadata;
  readonly onSuccess: (id: string) => void;
  readonly onTokenInvalid: () => void;
}) {
  const { t } = useTranslation();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  async function download() {
    setBusy(true);
    setError(false);
    try {
      await downloadPublicCensusTemplate(token);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }
  if (metadata.census_workbook?.received) return <CensusWorkbookReceipt />;
  return (
    <>
      <section className="mb-5 rounded-xl border border-slate-200 bg-white p-5">
        <h1 className="text-xl font-semibold">{t("census.handoff.public_title")}</h1>
        <p className="my-3 text-sm text-slate-600">{t("census.handoff.public_hint")}</p>
        <button
          type="button"
          disabled={busy}
          onClick={() => void download()}
          className="focus-visible:ring-brand-500 min-h-11 rounded-lg border px-3 py-2 focus-visible:ring-2 disabled:opacity-50"
        >
          {t(busy ? "census.import.template_loading" : "census.import.template_download")}
        </button>
        {error ? (
          <p role="alert" className="mt-2 text-red-700">
            {t("census.handoff.template_error")}
          </p>
        ) : null}
      </section>
      <IntakeForm
        token={token}
        projectTitle={metadata.project.title}
        limits={metadata.limits}
        acceptedTypesHint={t("census.handoff.xlsx_only")}
        onSuccess={onSuccess}
        onTokenInvalid={onTokenInvalid}
      />
    </>
  );
}

export function CensusWorkbookReceipt() {
  const { t } = useTranslation();
  return (
    <section className="rounded-xl bg-white p-6" role="status">
      <h1 className="text-xl font-semibold">{t("census.handoff.received")}</h1>
      <p className="mt-3">{t("census.handoff.receipt")}</p>
    </section>
  );
}
